import "./Carousel.css";

import React, { useEffect, useState } from "react";

import { IFullfilledMonthsResponse } from "../../services/Standard-of-care/standard-of-care.interface";
import MonthItem from "./MonthItem";
import { usePetCareStore } from "../../hooks/redux-hooks/pet-care-data/pet-care-data-store";

interface IMonthCarouselProps {
  data: IFullfilledMonthsResponse[];
  handleSocDataSelect: (data: IFullfilledMonthsResponse) => void;
}

const MonthCarousel: React.FC<IMonthCarouselProps> = ({ data, handleSocDataSelect }) => {
  const visibleMonths = 5;
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(null);
  const { petCareData } = usePetCareStore();
  const [direction, setDirection] = useState("next");


  const generateAllMonths = () => {
    const months = [];
    const start = new Date(new Date().getFullYear(), 0, 1);
    const end = new Date(new Date().getFullYear() + 1, 1, 1);

    while (start <= end) {
      months.push({
        monthName: start.toLocaleString("default", { month: "long" }),
        monthIndex: start.getMonth() + 1,
        year: start.getFullYear().toString(),
        status: false,
        fulfilledDate: null,
      });
      start.setMonth(start.getMonth() + 1);
    }

    return months;
  };

  const mergedData = generateAllMonths().map((month) => {
    const found = data.find((d) => d.monthIndex === month.monthIndex && d.year === month.year);

    return found ? found : month;
  });

  const currentMonthIndex = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const initialIndex = Math.max(0, mergedData.findIndex((month) =>
    month.monthIndex === currentMonthIndex) - Math.floor(visibleMonths / 2));
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const nextSlide = () => {
    setDirection("next");
    if (currentIndex + visibleMonths < mergedData.length) {
      setCurrentIndex(currentIndex + visibleMonths);
    } else {
      setCurrentIndex(mergedData.length - visibleMonths);
    }
  };

  const prevSlide = () => {
    setDirection("prev");
    if (currentIndex - visibleMonths >= 0) {
      setCurrentIndex(currentIndex - visibleMonths);
    } else {
      setCurrentIndex(0);
    }
  };

  const isNextDisabled = () => currentIndex + visibleMonths >= data.length;

  const isPrevDisabled = () => currentIndex === 0;

  const handleItemClick = (monthIndex: number) => {
    setSelectedMonthIndex(monthIndex);
    const selectedMonth = mergedData.find((month) => month.monthIndex === monthIndex);

    if (selectedMonth) {
      handleSocDataSelect(selectedMonth);
    }
  };

  useEffect(() =>{
    setSelectedMonthIndex(null);
  }, [petCareData]);

  const visibleItems = mergedData.slice(currentIndex, currentIndex + visibleMonths);

  return (
    <div className="carousel-container max-w-full mx-auto p-4 overflow-hidden relative">
      <div className={`carousel-track flex ${direction === "next" ? "slide-left" : "slide-right"}`}>
        {visibleItems.map((month, index) => (
          <React.Fragment key={`${month.monthName}-${month.year}`}>
            {(month.monthIndex === 1 && month.year !== currentYear.toString()) && (
              <div className="carousel-item w-1/5 px-3 py-2 text-gray-400 flex items-center">
                <div className="ps-3 flex items-center text-xs border-gray-4 border-l-2 h-full">
                  {currentYear + 1}
                </div>
              </div>
            )}
            <MonthItem
              monthName={month.monthName}
              monthIndex={month.monthIndex}
              currentMonthIndex={currentMonthIndex}
              year={month.year}
              status={month.status}
              isGrayedOut={month.monthIndex > currentMonthIndex ||
                !data.some((d) => d.monthIndex === month.monthIndex && d.year === month.year)}
              selected={month.monthIndex === selectedMonthIndex}
              onClick={() => handleItemClick(month.monthIndex)}
              fulfilledDate={month.fulfilledDate}
            />
          </React.Fragment>
        ))}
      </div>
      <button onClick={prevSlide} className="carousel-button left-0" disabled={isPrevDisabled()}>❮</button>
      <button onClick={nextSlide} className="carousel-button right-0" disabled={isNextDisabled()}>❯</button>
    </div>
  );
};

export default MonthCarousel;
